import { useState, useMemo } from "react";
import Layout from "@/components/feature/Layout";
import { siparislerData, type SiparisRecord } from "@/mocks/siparisler";
import { musterilerData } from "@/mocks/musteriler";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

const durumColors: Record<string, string> = {
  "Alındı": "bg-gray-500/10 text-gray-400",
  "Hazırlanıyor": "bg-gold-500/10 text-gold-400",
  "Atölyede": "bg-sky-500/10 text-sky-400",
  "Teslime Hazır": "bg-emerald-500/10 text-emerald-400",
  "Teslim Edildi": "bg-green-500/10 text-green-400",
  "İptal": "bg-red-500/10 text-red-400",
};

const durumOptions = ["Tümü", "Alındı", "Hazırlanıyor", "Atölyede", "Teslime Hazır", "Teslim Edildi", "İptal"];

export default function Siparisler() {
  const [search, setSearch] = useState("");
  const [durumFilter, setDurumFilter] = useState("Tümü");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "card">("table");
  const [selectedMusteri, setSelectedMusteri] = useState<number | "">("");
  const [selectedEditMusteri, setSelectedEditMusteri] = useState<number | "">("");

  const [data, setData] = useState<SiparisRecord[]>(siparislerData);

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        item.musteriAdi.toLowerCase().includes(search.toLowerCase()) ||
        item.urunAciklama.toLowerCase().includes(search.toLowerCase()) ||
        item.telefon.includes(search);
      const matchesDurum = durumFilter === "Tümü" || item.durum === durumFilter;
      return matchesSearch && matchesDurum;
    });
  }, [data, search, durumFilter]);

  const totalSiparis = filtered.length;
  const totalKapora = filtered.reduce((s, i) => s + i.kapora, 0);
  const totalKalan = filtered.reduce((s, i) => s + i.kalanOdeme, 0);

  const musteriMap = useMemo(() => {
    const map: Record<number, { adSoyad: string; telefon: string }> = {};
    musterilerData.forEach((m) => {
      map[m.id] = { adSoyad: m.adSoyad, telefon: m.telefon };
    });
    return map;
  }, []);

  const handleMusteriChange = (musteriId: number | "", formType: "add" | "edit") => {
    if (formType === "add") {
      setSelectedMusteri(musteriId);
    } else {
      setSelectedEditMusteri(musteriId);
    }
  };

  const handleAdd = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const kapora = Number(fd.get("kapora"));
    const toplam = Number(fd.get("toplamTutar"));
    const yeni: SiparisRecord = {
      id: Date.now(),
      musteriAdi: String(fd.get("musteriAdi") || ""),
      telefon: String(fd.get("telefon") || ""),
      siparisTuru: String(fd.get("siparisTuru") || ""),
      urunAciklama: String(fd.get("urunAciklama") || ""),
      detaylar: String(fd.get("detaylar") || ""),
      siparisTarihi: String(fd.get("siparisTarihi") || ""),
      teslimTarihi: String(fd.get("teslimTarihi") || ""),
      kapora,
      kalanOdeme: toplam - kapora,
      toplamTutar: toplam,
      durum: (fd.get("durum") as SiparisRecord["durum"]) || "Alındı",
    };
    setData((prev) => [yeni, ...prev]);
    setShowAddForm(false);
    setSelectedMusteri("");
    form.reset();
  };

  const handleEdit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingId) return;
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const kapora = Number(fd.get("kapora"));
    const toplam = Number(fd.get("toplamTutar"));
    setData((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              musteriAdi: String(fd.get("musteriAdi") || ""),
              telefon: String(fd.get("telefon") || ""),
              siparisTuru: String(fd.get("siparisTuru") || ""),
              urunAciklama: String(fd.get("urunAciklama") || ""),
              detaylar: String(fd.get("detaylar") || ""),
              siparisTarihi: String(fd.get("siparisTarihi") || ""),
              teslimTarihi: String(fd.get("teslimTarihi") || ""),
              kapora,
              kalanOdeme: toplam - kapora,
              toplamTutar: toplam,
              durum: (fd.get("durum") as SiparisRecord["durum"]) || item.durum,
            }
          : item
      )
    );
    setEditingId(null);
    setSelectedEditMusteri("");
    form.reset();
  };

  const handleDelete = (id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const editRecord = data.find((d) => d.id === editingId);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">Sipariş Takibi</h1>
        <p className="text-sm text-gray-500">Müşteri siparişlerini durum bazında takip edin ve yönetin.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Sipariş</p>
          <p className="text-xl font-semibold text-gray-100">{totalSiparis} adet</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Kapora</p>
          <p className="text-xl font-semibold text-emerald-400">{formatCurrency(totalKapora)}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Kalan Ödeme</p>
          <p className="text-xl font-semibold text-gold-400">{formatCurrency(totalKalan)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <i className="ri-search-line text-gray-500 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sipariş ara..."
              className="bg-transparent text-sm text-gray-300 placeholder-gray-500 outline-none w-48"
            />
          </div>
          <select value={durumFilter} onChange={(e) => setDurumFilter(e.target.value)} className="input-dark">
            {durumOptions.map((d) => (
              <option key={d} value={d} className="bg-dark-950">{d}</option>
            ))}
          </select>
          <div className="flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden">
            <button onClick={() => setViewMode("table")} className={`px-3 py-2 text-sm cursor-pointer ${viewMode === "table" ? "bg-gold-500/20 text-gold-400" : "text-gray-400"}`}>
              <i className="ri-table-line" />
            </button>
            <button onClick={() => setViewMode("card")} className={`px-3 py-2 text-sm cursor-pointer ${viewMode === "card" ? "bg-gold-500/20 text-gold-400" : "text-gray-400"}`}>
              <i className="ri-layout-grid-line" />
            </button>
          </div>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-gold">
          <i className="ri-add-line" />
          Yeni Sipariş
        </button>
      </div>

      {/* Table View */}
      {viewMode === "table" && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Müşteri</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Ürün</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Detay</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Toplam</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Kapora</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Kalan</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Teslim</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Durum</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="table-row-hover border-b border-white/5 last:border-0">
                    <td className="px-4 py-3 text-sm text-gray-200 font-medium">{item.musteriAdi}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{item.urunAciklama}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{item.detaylar}</td>
                    <td className="px-4 py-3 text-sm text-gray-200 text-right font-medium">{formatCurrency(item.toplamTutar)}</td>
                    <td className="px-4 py-3 text-sm text-emerald-400 text-right">{formatCurrency(item.kapora)}</td>
                    <td className="px-4 py-3 text-sm text-gold-400 text-right">{formatCurrency(item.kalanOdeme)}</td>
                    <td className="px-4 py-3 text-sm text-gray-400">{item.teslimTarihi}</td>
                    <td className="px-4 py-3">
                      <span className={`status-badge ${durumColors[item.durum] || ""}`}>{item.durum}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`https://wa.me/${item.telefon.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(item.musteriAdi)},%20${encodeURIComponent(item.urunAciklama)}%20siparişiniz%20hakkında%20bilgi%20vermek%20istiyoruz.`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                          title="WhatsApp"
                        >
                          <i className="ri-whatsapp-line" />
                        </a>
                        <button onClick={() => setEditingId(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer" title="Düzenle">
                          <i className="ri-edit-line" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer" title="Sil">
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">Kayıt bulunamadı.</div>
          )}
        </div>
      )}

      {/* Card View */}
      {viewMode === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="glass-card p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-200">{item.musteriAdi}</h4>
                  <p className="text-xs text-gray-500">{item.telefon}</p>
                </div>
                <span className={`status-badge ${durumColors[item.durum] || ""}`}>{item.durum}</span>
              </div>
              <p className="text-sm text-gray-300 mb-1">{item.urunAciklama}</p>
              <p className="text-xs text-gray-500 mb-3">{item.detaylar}</p>
              <div className="flex items-center justify-between text-xs mb-3">
                <span className="text-gray-500">Sipariş: {item.siparisTarihi}</span>
                <span className="text-gold-400 font-medium">Teslim: {item.teslimTarihi}</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-gray-500">Toplam</p>
                  <p className="text-sm font-medium text-gray-200">{formatCurrency(item.toplamTutar)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Kapora</p>
                  <p className="text-sm font-medium text-emerald-400">{formatCurrency(item.kapora)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Kalan</p>
                  <p className="text-sm font-medium text-gold-400">{formatCurrency(item.kalanOdeme)}</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
                <a
                  href={`https://wa.me/${item.telefon.replace(/\s/g, "")}?text=Merhaba%20${encodeURIComponent(item.musteriAdi)},%20${encodeURIComponent(item.urunAciklama)}%20siparişiniz%20hakkında%20bilgi%20vermek%20istiyoruz.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors cursor-pointer"
                  title="WhatsApp"
                >
                  <i className="ri-whatsapp-line" />
                </a>
                <button onClick={() => setEditingId(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer" title="Düzenle">
                  <i className="ri-edit-line" />
                </button>
                <button onClick={() => handleDelete(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer" title="Sil">
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full p-8 text-center text-gray-500 text-sm">Kayıt bulunamadı.</div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Yeni Sipariş Ekle</h3>
              <button onClick={() => setShowAddForm(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Müşteri Rehberi</label>
                <select
                  value={selectedMusteri}
                  onChange={(e) => {
                    const val = e.target.value === "" ? "" : Number(e.target.value);
                    handleMusteriChange(val, "add");
                  }}
                  className="input-dark w-full"
                >
                  <option value="" className="bg-dark-950">Manuel gir veya rehberden seç...</option>
                  {musterilerData.map((m) => (
                    <option key={m.id} value={m.id} className="bg-dark-950">
                      {m.adSoyad} — {m.telefon}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Müşteri Adı</label>
                <input
                  name="musteriAdi"
                  required
                  className="input-dark w-full"
                  placeholder="Müşteri adı"
                  value={selectedMusteri !== "" ? musteriMap[selectedMusteri]?.adSoyad || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedMusteri !== ""}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Telefon</label>
                <input
                  name="telefon"
                  required
                  className="input-dark w-full"
                  placeholder="+90 555 123 4567"
                  value={selectedMusteri !== "" ? musteriMap[selectedMusteri]?.telefon || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedMusteri !== ""}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Sipariş Türü</label>
                  <input name="siparisTuru" required className="input-dark w-full" placeholder="Yüzük, Kolye..." />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Toplam Tutar</label>
                  <input name="toplamTutar" type="number" required min={0} className="input-dark w-full" placeholder="0" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Ürün Açıklaması</label>
                <input name="urunAciklama" required className="input-dark w-full" placeholder="Ürün adı" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Detaylar</label>
                <textarea name="detaylar" rows={2} className="input-dark w-full resize-none" placeholder="Ölçü, gram, ayar, taş bilgisi..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Sipariş Tarihi</label>
                  <input name="siparisTarihi" type="date" required className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Teslim Tarihi</label>
                  <input name="teslimTarihi" type="date" required className="input-dark w-full" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Kapora</label>
                  <input name="kapora" type="number" required min={0} defaultValue={0} className="input-dark w-full" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Durum</label>
                  <select name="durum" className="input-dark w-full" defaultValue="Alındı">
                    {durumOptions.slice(1).map((d) => (
                      <option key={d} value={d} className="bg-dark-950">{d}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingId && editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Sipariş Düzenle</h3>
              <button onClick={() => setEditingId(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Müşteri Rehberi</label>
                <select
                  value={selectedEditMusteri}
                  onChange={(e) => {
                    const val = e.target.value === "" ? "" : Number(e.target.value);
                    handleMusteriChange(val, "edit");
                  }}
                  className="input-dark w-full"
                >
                  <option value="" className="bg-dark-950">Mevcut müşteri veya rehberden değiştir...</option>
                  {musterilerData.map((m) => (
                    <option key={m.id} value={m.id} className="bg-dark-950">
                      {m.adSoyad} — {m.telefon}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Müşteri Adı</label>
                <input
                  name="musteriAdi"
                  required
                  className="input-dark w-full"
                  defaultValue={editRecord.musteriAdi}
                  value={selectedEditMusteri !== "" ? musteriMap[selectedEditMusteri]?.adSoyad || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedEditMusteri !== ""}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Telefon</label>
                <input
                  name="telefon"
                  required
                  className="input-dark w-full"
                  defaultValue={editRecord.telefon}
                  value={selectedEditMusteri !== "" ? musteriMap[selectedEditMusteri]?.telefon || "" : undefined}
                  onChange={() => {}}
                  readOnly={selectedEditMusteri !== ""}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Sipariş Türü</label>
                  <input name="siparisTuru" defaultValue={editRecord.siparisTuru} required className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Toplam Tutar</label>
                  <input name="toplamTutar" type="number" defaultValue={editRecord.toplamTutar} required min={0} className="input-dark w-full" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Ürün Açıklaması</label>
                <input name="urunAciklama" defaultValue={editRecord.urunAciklama} required className="input-dark w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Detaylar</label>
                <textarea name="detaylar" rows={2} defaultValue={editRecord.detaylar} className="input-dark w-full resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Sipariş Tarihi</label>
                  <input name="siparisTarihi" type="date" defaultValue={editRecord.siparisTarihi} required className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Teslim Tarihi</label>
                  <input name="teslimTarihi" type="date" defaultValue={editRecord.teslimTarihi} required className="input-dark w-full" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Kapora</label>
                  <input name="kapora" type="number" defaultValue={editRecord.kapora} required min={0} className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Durum</label>
                  <select name="durum" className="input-dark w-full" defaultValue={editRecord.durum}>
                    {durumOptions.slice(1).map((d) => (
                      <option key={d} value={d} className="bg-dark-950">{d}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingId(null)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
